import { SettingsPermissions } from "@redotech/account-common/permissions";
import { directMerchantAccess } from "../../settings-catalog/access";
import type { SettingGroupDefinition } from "../../settings-catalog/types";

export type FulfillmentFlatTeamSettingGroupId =
  | "fulfillment.ship_from_locations"
  | "fulfillment.address"
  | "fulfillment.import_restrictions"
  | "fulfillment.printing";

export const FulfillmentFlatTeamSettingGroups = [
  {
    id: "fulfillment.ship_from_locations",
    title: "Ship from locations",
    description:
      "Locations used as shipping origins, including for manual order creation.",
    productArea: "fulfillment",
    access: directMerchantAccess(
      SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
    ),
    owner: {
      kind: "teamSettings",
      path: "settings.outboundLabels",
      product: "fulfillment",
    },
    risk: {
      level: "medium",
      reason: "Changes affect the origin selected for new manual orders.",
    },
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: { type: "settings", path: "shipping" },
    synonyms: [
      "ship from locations",
      "shipping origins",
      "warehouses",
      "fulfillment locations",
    ],
  },
  {
    id: "fulfillment.address",
    title: "Address verification",
    description:
      "How Redo verifies shipping addresses and what happens when verification fails, including customer outreach emails and automatic holds.",
    productArea: "fulfillment",
    access: directMerchantAccess(
      SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
    ),
    owner: {
      kind: "teamSettings",
      path: "settings.outboundLabels.address",
      product: "fulfillment",
    },
    risk: {
      level: "medium",
      reason:
        "Changes affect emails sent to customers and whether orders are held from fulfillment.",
    },
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: { type: "settings", path: "oms/general" },
    synonyms: [
      "address verification",
      "address validation",
      "bad address",
      "address correction email",
      "address outreach",
      "international addresses",
    ],
  },
  {
    id: "fulfillment.import_restrictions",
    title: "Import restrictions",
    description:
      "Import rules your catalog has tripped in screened cross-border markets, and whether you have marked each one resolved because you hold the required permit, certificate, or documentation. Resolving a rule stops it blocking publishing and pricing for every product that trips it.",
    productArea: "fulfillment",
    access: directMerchantAccess(
      SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
    ),
    owner: {
      kind: "crdb",
      path: "restriction_measure_resolution",
      product: "fulfillment",
    },
    risk: {
      level: "high",
      reason:
        "Resolving a restriction publishes and prices products a destination country flagged as restricted; doing it without the real documentation risks customs seizure.",
    },
    agentEditability: {
      mode: "never",
      reason:
        "Only the merchant can attest that they hold the documentation a restriction requires.",
    },
    fullPageTarget: { type: "settings", path: "international/restrictions" },
    synonyms: [
      "import restrictions",
      "restricted products",
      "customs restrictions",
      "certificate of conformity",
      "permit required",
      "prohibited items",
      "resolve restriction",
    ],
  },
  {
    id: "fulfillment.printing",
    title: "Printing",
    description:
      "How shipping labels, packing slips and other fulfillment documents are produced and ordered when a warehouse prints them.",
    productArea: "fulfillment",
    access: directMerchantAccess(
      SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
    ),
    owner: {
      kind: "teamSettings",
      path: "settings.outboundLabels",
      product: "fulfillment",
    },
    risk: {
      level: "medium",
      reason:
        "Changes what a warehouse physically prints and packs against, so a wrong setting can put the wrong items in a box.",
    },
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: { type: "settings", path: "oms/printing" },
    synonyms: [
      "printing settings",
      "print documents",
      "warehouse printing",
      "label and slip printing",
    ],
  },
] as const satisfies readonly SettingGroupDefinition[];

export const FulfillmentFlatTeamSettingGroupById = Object.fromEntries(
  FulfillmentFlatTeamSettingGroups.map((group) => [group.id, group]),
) as Record<
  FulfillmentFlatTeamSettingGroupId,
  (typeof FulfillmentFlatTeamSettingGroups)[number]
>;
